import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Loader2, MapPin, Plus, User } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Card, CardContent } from '@/components/atoms/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Label } from '@/components/atoms/label';
import { Textarea } from '@/components/atoms/textarea';
import { formatCurrency } from '@/components/molecules/financialImpact';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { hasLivePortalSession } from '@/stores/portalAuth.store';
import {
  usePortalCancelPreview,
  usePortalCancelVisit,
  usePortalRescheduleVisit,
  usePortalVisitAlternatives,
  usePortalVisits,
  type PortalVisit,
} from '@/hooks/portal/usePortalVisits';
import { usePortalIdentity } from '@/hooks/portal/usePortalAuth';
import { PortalShell } from '@/components/portal/PortalShell';
import { PortalStatusPill } from '@/components/portal/PortalStatusPill';
import { PortalVisitDateBlock } from '@/components/portal/PortalVisitDateBlock';
import { portalVisitPill, portalVisitWhen } from '@/lib/portal/visitDisplay';
import { PortalSlotPicker } from '@/components/portal/PortalSlotPicker';
import { PortalBookVisitDialog } from '@/components/portal/PortalBookVisitDialog';
import { ApiError } from '@/lib/api/errors';
import { PORTAL_ACCENT_BTN } from '@/lib/portal/documentHelpers';

/** Statuses a customer may still self-manage — before a tech is dispatched. */
const MANAGEABLE = new Set(['SCHEDULED', 'CONFIRMED']);

export const Route = createFileRoute('/(portal)/portal/visits/')({
  beforeLoad: () => {
    if (!hasLivePortalSession()) {
      throw redirect({ to: '/portal/login' });
    }
  },
  component: PortalVisitsPage,
});

function PortalVisitsPage() {
  const visits = usePortalVisits();

  return (
    <PortalShell>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Service history</h1>
          <p className="text-sm text-muted-foreground">
            Every visit to your property, upcoming and past.
          </p>
        </div>
        <PortalBookVisitDialog
          trigger={
            <Button size="sm">
              <Plus className="mr-1.5 size-4" />
              Book a visit
            </Button>
          }
        />
      </div>

      {visits.isPending ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : visits.isError ? (
        <Card>
          <CardContent className="space-y-3 py-6 text-sm text-muted-foreground">
            <p>We couldn&apos;t load your visits. Please try again.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void visits.refetch()}
              disabled={visits.isFetching}
            >
              {visits.isFetching ? <Loader2 className="size-4 animate-spin" /> : null}
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : visits.data.upcoming.length === 0 && visits.data.past.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            You don&apos;t have any visits yet.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {visits.data.upcoming.length > 0 ? (
            <VisitGroup title="Upcoming" visits={visits.data.upcoming} />
          ) : null}
          {visits.data.past.length > 0 ? (
            <VisitGroup title="Past" visits={visits.data.past} />
          ) : null}
        </div>
      )}
    </PortalShell>
  );
}

function VisitGroup({ title, visits }: { title: string; visits: PortalVisit[] }) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      {visits.map((v) => (
        <VisitCard key={v.appointmentId} visit={v} />
      ))}
    </div>
  );
}

function arrivalLabel(v: PortalVisit): string | null {
  if (!v.arrivalWindow) return null;
  const start = formatDateTimeInTimeZone(v.arrivalWindow.start, v.timezone, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const end = formatDateTimeInTimeZone(v.arrivalWindow.end, v.timezone, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `Arrival window ${start}–${end}`;
}

function addressLabel(v: PortalVisit): string | null {
  const a = v.serviceAddress;
  if (!a) return null;
  return [a.street, a.city, a.state, a.zip].filter(Boolean).join(', ') || null;
}

function VisitCard({ visit }: { visit: PortalVisit }) {
  const status = portalVisitPill(visit);
  const arrival = arrivalLabel(visit);
  const address = addressLabel(visit);
  return (
    <Card>
      <CardContent className="py-3.5">
        <div className="flex gap-3">
          <PortalVisitDateBlock visit={visit} />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{visit.serviceName ?? visit.label}</p>
                <p className="text-xs text-muted-foreground">{portalVisitWhen(visit)}</p>
              </div>
              <PortalStatusPill label={status.label} tone={status.tone} />
            </div>
            {arrival ? <p className="text-xs text-muted-foreground">{arrival}</p> : null}
            {visit.technicianName ? (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <User className="size-3.5" />
                {visit.technicianName}
              </p>
            ) : null}
            {address ? (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <MapPin className="size-3.5" />
                {address}
              </p>
            ) : null}
            {MANAGEABLE.has(visit.status) ? <VisitActions visit={visit} /> : null}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** "Tue, Sep 29, 8:00 AM – 10:00 AM" for a window, in the given zone. */
function windowLabel(startAt: string, endAt: string, timezone: string | null): string {
  const day = formatDateTimeInTimeZone(startAt, timezone, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const time = (instant: string) =>
    formatDateTimeInTimeZone(instant, timezone, { hour: 'numeric', minute: '2-digit' });
  return `${day}, ${time(startAt)} – ${time(endAt)}`;
}

/**
 * Customer self-service on a still-upcoming visit: move it to another arrival
 * window, or cancel. The windows are the ones the visit's own crew can take;
 * the move holds the chosen one and re-promises the visit server-side. Cancel
 * shows the cancellation fee (fetched read-only) BEFORE confirming, because a
 * customer cancel charges the card on file when a fee applies.
 */
function VisitActions({ visit }: { visit: PortalVisit }) {
  const [mode, setMode] = useState<'idle' | 'reschedule' | 'cancel'>('idle');
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const alternatives = usePortalVisitAlternatives(visit.appointmentId, mode === 'reschedule');
  const cancelPreview = usePortalCancelPreview(visit.appointmentId, mode === 'cancel');
  const reschedule = usePortalRescheduleVisit(visit.appointmentId);
  const cancel = usePortalCancelVisit(visit.appointmentId);
  const identity = usePortalIdentity();
  const businessName = identity.data?.tenant.name ?? 'the business';

  const reset = () => {
    setMode('idle');
    setSelectedOffer(null);
    setSelectedRowId(null);
    setReason('');
  };

  const submitReschedule = async () => {
    if (!selectedOffer) return;
    try {
      const moved = await reschedule.mutateAsync({ offerId: selectedOffer });
      toast.success(
        moved.window.startAt && moved.window.endAt
          ? `Visit moved to ${windowLabel(moved.window.startAt, moved.window.endAt, moved.timezone)}.`
          : 'Visit moved.',
      );
      reset();
    } catch (err) {
      // A 409 is the genuine "time just got taken" race — reload the offered
      // windows and clear the dead selection so the customer picks a fresh one,
      // instead of re-submitting the same now-unavailable window in a loop.
      if (err instanceof ApiError && err.status === 409) {
        toast.error('That time was just taken — please pick another.');
        setSelectedOffer(null);
        setSelectedRowId(null);
        void alternatives.refetch();
      } else if (err instanceof ApiError && err.status === 422) {
        toast.error(`This visit can’t be moved online. Please contact ${businessName}.`);
      } else {
        toast.error('We couldn’t reschedule this visit. Please try again.');
      }
    }
  };

  const submitCancel = async () => {
    try {
      const res = await cancel.mutateAsync(reason.trim() || undefined);
      if (res.status === 'pending_approval') {
        // The business requires a person to approve this cancellation — it is
        // NOT cancelled yet, so don't tell the customer it was.
        toast.success('Cancellation requested. The business will confirm shortly.');
      } else {
        const fee = res.cancellationFee;
        toast.success(
          fee
            ? `Visit cancelled. A ${formatCurrency(fee.amount)} fee was applied.`
            : 'Visit cancelled.',
        );
      }
      reset();
    } catch {
      toast.error('We couldn’t cancel this visit. Please try again.');
    }
  };

  const windows = alternatives.data?.windows ?? [];
  const planDays = alternatives.data?.recurringWindowDays ?? null;
  const fee = cancelPreview.data?.feeAmount ?? null;
  // The fee must be KNOWN before we let the customer confirm: the server always
  // re-resolves and charges it, so a failed preview must NOT fall through to a
  // false "no fee" + an enabled confirm (that would charge a fee they never saw).
  const feeKnown = cancelPreview.isSuccess;

  return (
    <>
      {mode === 'cancel' ? null : (
        <div className="flex gap-2 border-t pt-2">
          <Button variant="outline" size="sm" onClick={() => setMode('reschedule')}>
            Reschedule
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setMode('cancel')}>
            Cancel visit
          </Button>
        </div>
      )}

      {/* Reschedule — a centered "Choose a time" modal, matching the book flow. */}
      <Dialog
        open={mode === 'reschedule'}
        onOpenChange={(open) => {
          if (!open) reset();
        }}
      >
        <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:max-w-md">
          <DialogHeader className="shrink-0 border-b border-border px-6 pt-6 pb-4">
            <DialogTitle className="text-xl tracking-tight">Choose a new time</DialogTitle>
            <DialogDescription className="sr-only">
              Pick a new time for your visit.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
            <p className="text-sm text-muted-foreground">
              Pick a day, then the window your technician can arrive in.
            </p>
            {planDays !== null ? (
              <p className="rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
                Visits in your plan can move up to {planDays} {planDays === 1 ? 'day' : 'days'} from
                their date. To move this one further, contact {businessName}.
              </p>
            ) : null}
            {alternatives.isPending ? (
              // Checking each window against the technician's route takes a few
              // seconds; say so, so the wait reads as work rather than a hang.
              <div className="flex flex-col items-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="size-5 animate-spin" />
                Finding open times…
              </div>
            ) : alternatives.isError ? (
              <p className="py-4 text-sm text-muted-foreground">
                {alternatives.error instanceof ApiError && alternatives.error.status === 422
                  ? `This visit can’t be moved online. Please contact ${businessName}.`
                  : 'We couldn’t load available times. Please try again.'}
              </p>
            ) : windows.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">
                {planDays !== null
                  ? `No other times are open within ${planDays} ${planDays === 1 ? 'day' : 'days'} of this visit. To move it further, contact ${businessName}.`
                  : `No other times are open in the next two weeks. Contact ${businessName} to find another time.`}
              </p>
            ) : (
              <PortalSlotPicker
                slots={windows.map((w) => ({ key: w.offerId, startAt: w.startAt, endAt: w.endAt }))}
                timezone={alternatives.data?.timezone ?? null}
                selectedRowId={selectedRowId}
                onSelect={(key, _startAt, rowId) => {
                  setSelectedOffer(key);
                  setSelectedRowId(rowId);
                }}
              />
            )}
            <p className="text-xs text-muted-foreground">
              You&apos;ll get a text when your technician is on the way.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 border-t border-border px-6 py-4">
            <Button variant="outline" onClick={reset} disabled={reschedule.isPending}>
              Cancel
            </Button>
            <Button
              className={PORTAL_ACCENT_BTN}
              disabled={!selectedOffer || reschedule.isPending}
              onClick={() => void submitReschedule()}
            >
              {reschedule.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Confirm
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {mode === 'cancel' ? (
        <div className="space-y-2 border-t pt-2">
          <p className="text-xs font-medium">Cancel this visit?</p>
          {cancelPreview.isPending ? (
            <div className="flex justify-center py-3">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </div>
          ) : cancelPreview.isError ? (
            <p className="rounded-md border border-warning-border bg-warning-surface px-3 py-2 text-xs text-warning">
              We couldn&apos;t check whether a cancellation fee applies.{' '}
              <button
                type="button"
                className="font-semibold underline underline-offset-2"
                onClick={() => void cancelPreview.refetch()}
              >
                Try again
              </button>
              .
            </p>
          ) : fee && fee > 0 ? (
            <p className="rounded-md border border-warning-border bg-warning-surface px-3 py-2 text-xs text-warning">
              A cancellation fee of <span className="font-semibold">{formatCurrency(fee)}</span>{' '}
              applies and will be charged to your payment method on file.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">No cancellation fee applies.</p>
          )}
          <div className="space-y-1">
            <Label htmlFor={`cancel-reason-${visit.appointmentId}`} className="text-xs">
              Reason (optional)
            </Label>
            <Textarea
              id={`cancel-reason-${visit.appointmentId}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Let the business know why (optional)"
            />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="destructive"
              className="flex-1"
              disabled={cancel.isPending || cancelPreview.isPending || !feeKnown}
              onClick={() => void submitCancel()}
            >
              {cancel.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Confirm cancellation
            </Button>
            <Button variant="ghost" size="sm" onClick={reset} disabled={cancel.isPending}>
              Back
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
